// client/src/pages/ClientListPage.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import * as clientsApi from '../api/clients'; // Our frontend clients API service
import * as quotesApi from '../api/quotes'; // Our frontend quotes API service
import LoadingSpinner from '../components/LoadingSpinner';

function ClientListPage() {
  const { loading: authLoading, token } = useAuth();
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientQuotes, setClientQuotes] = useState([]);
  const [quotesLoading, setQuotesLoading] = useState(false);
  const [quotesError, setQuotesError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleClientClick = async (client) => {
    setSelectedClient(client);
    setQuotesLoading(true);
    setQuotesError(null);
    setIsModalOpen(true);
    try {
      const filters = { clientId: client.id };
      const response = await quotesApi.fetchQuotes(filters);
      let quotes = response.quotes || [];
      // Fallback: Manually filter quotes by clientId in case API doesn't filter
      quotes = quotes.filter(quote => quote.clientId === client.id || (quote.client && quote.client.id === client.id));
      setClientQuotes(quotes);
    } catch (err) {
      setQuotesError(err.message || 'Failed to load quotes for this client.');
      setClientQuotes([]);
    } finally {
      setQuotesLoading(false);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedClient(null);
    setClientQuotes([]);
    setQuotesError(null);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'draft': return 'bg-gray-200 text-gray-800';
      case 'sent': return 'bg-blue-100 text-blue-800';
      case 'accepted': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      case 'archived': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  // Filter clients based on search term
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredClients(clients);
    } else {
      const term = searchTerm.toLowerCase();
      setFilteredClients(
        clients.filter(client => 
          client.name.toLowerCase().includes(term) || 
          (client.email && client.email.toLowerCase().includes(term)) || 
          (client.phone && client.phone.toLowerCase().includes(term))
        )
      );
    }
    setCurrentPage(1); // Reset to first page on search change
  }, [searchTerm, clients]);

  // Calculate pagination
  const totalItems = filteredClients.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedClients = filteredClients.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo(0, 0); // Scroll to top on page change
  };

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1); // Reset to first page on items per page change
  };

  useEffect(() => {
    // Fetch clients when component mounts
    const getClients = async () => {
      if (authLoading) {
        setLoading(false); // If auth is still loading, stop local loading
        return;
      }

      try {
        setError(null);
        setSuccess(null);
        setLoading(true);
        const response = await clientsApi.fetchClients(token);
        setClients(response.clients); // Assuming API returns { clients: [...] }
      } catch (err) {
        console.error('Failed to fetch clients:', err);
        setError(err.message || 'Failed to load clients.');
      } finally {
        setLoading(false);
      }
    };

    getClients();
  }, [authLoading]); // Dependency array: re-run when authLoading changes

  const handleDelete = async (clientId) => {
    if (!window.confirm('Are you sure you want to delete this client? This action cannot be undone, and will prevent new quotes from being created for this client.')) {
      return; // User cancelled
    }
    setLoading(true); // Indicate loading while deleting
    try {
      setError(null);
      setSuccess(null);
      await clientsApi.deleteClient(token, clientId);
      setSuccess('Client deleted successfully!');
      // Remove the deleted client from the local state
      setClients(prevClients => prevClients.filter(client => client.id !== clientId));
    } catch (err) {
      console.error('Failed to delete client:', err);
      setError(err.message || 'Failed to delete client. Check if there are associated quotes that need to be deleted first.');
    } finally {
      setLoading(false);
    }
  };

  if (authLoading || loading) {
    return <LoadingSpinner message="Loading Clients..." />;
  }

  if (error) {
    return (
      <div className="min-h-[calc(100vh-64px)] flex items-center justify-center bg-gray-50 p-4">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-md max-w-md text-center" role="alert">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-64px)] bg-gray-50 p-4 sm:p-6 lg:p-8">
      <div className="max-w-4xl mx-auto bg-white p-6 sm:p-8 rounded-xl shadow-lg border border-gray-200">
        {/* Header and Controls */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <h2 className="text-3xl font-bold text-primary-700">Your Clients</h2>
          <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
            <Link
              to="/clients/new"
              className="px-4 py-2 bg-primary-600 text-white font-medium rounded-lg shadow-md hover:bg-primary-700 transition duration-300 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 text-center"
            >
              + Add New Client
            </Link>
          </div>
        </div>

        {/* Search Bar - Full Width */}
        <div className="mb-6 relative">
          <input
            type="text"
            placeholder="Search clients..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-primary-500 focus:border-primary-500 text-sm"
          />
          <svg className="absolute right-3 top-2.5 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
          </svg>
        </div>

        {success && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-md relative mb-4" role="alert">
            <span className="block sm:inline">{success}</span>
          </div>
        )}

        {clients.length === 0 ? (
          <div className="text-center py-12 border-2 border-dashed border-gray-300 rounded-lg">
            <h3 className="text-xl font-semibold text-gray-700">No clients yet</h3>
            <p className="text-gray-500 mt-2 mb-4">Add your first client to start creating quotes and invoices.</p>
            <Link
              to="/clients/new"
              className="px-6 py-3 bg-primary-600 text-white font-bold rounded-lg shadow-lg hover:bg-primary-700 transition duration-300 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 new-client-button"
            >
              + Add Your First Client
            </Link>
          </div>
        ) : filteredClients.length === 0 ? (
          <p className="text-gray-600 text-center py-8 border border-dashed border-gray-300 rounded-lg">
            No clients found matching "{searchTerm}". Try a different search term.
          </p>
        ) : (
          <div>
            {/* Desktop Table View (hidden on small screens) */}
            <div className="hidden md:block overflow-x-auto rounded-lg border border-gray-200 shadow-sm">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-blue-50">
                  <tr>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-blue-700 uppercase tracking-wider">
                      Client Name
                    </th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-blue-700 uppercase tracking-wider">
                      Email
                    </th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-blue-700 uppercase tracking-wider">
                      Phone
                    </th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-blue-700 uppercase tracking-wider">
                      Address
                    </th>
                    <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-blue-700 uppercase tracking-wider">
                      Actions
                    </th> 
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {paginatedClients.map((client) => (
                    <tr key={client.id} className="hover:bg-gray-50 transition duration-150 ease-in-out">
                      <td className="px-4 py-3 whitespace-nowrap">
                        <Link 
                          to={`/clients/${client.id}`}
                          className="text-sm font-medium text-gray-900 hover:text-primary-600 cursor-pointer"
                          title={`View client overview for ${client.name}`}
                        >
                          {client.name}
                        </Link>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                        {client.email ? (
                          <a href={`mailto:${client.email}`} className="text-gray-900 hover:text-primary-600" title={`Email ${client.name}`}>
                            {client.email}
                          </a>
                        ) : 'N/A'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                        {client.phone ? (
                          <a href={`tel:${client.phone}`} className="text-gray-900 hover:text-primary-600" title={`Call ${client.name}`}>
                            {client.phone}
                          </a>
                        ) : 'N/A'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 truncate max-w-[150px] sm:max-w-xs">
                        {client.address ? (
                          <a 
                            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(client.address)}`} 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            className="text-gray-900 hover:text-primary-600" 
                            title={`View ${client.name}'s address on Google Maps`}
                          >
                            {client.address}
                          </a>
                        ) : 'No address'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end space-x-2">
                          <Link
                            to={`/clients/edit/${client.id}`}
                            className="text-primary-600 hover:text-primary-900 p-1 rounded-full hover:bg-primary-50 transition duration-150 ease-in-out"
                            aria-label={`Edit ${client.name}`}
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path></svg>
                          </Link>
                          <button
                            onClick={() => handleDelete(client.id)}
                            className="text-red-600 hover:text-red-900 p-1 rounded-full hover:bg-red-50 transition duration-150 ease-in-out"
                            aria-label={`Delete ${client.name}`}
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path></svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
 
            {/* Mobile Card View (visible on small screens, hidden on md and up) */}
            <div className="md:hidden space-y-4">
              {paginatedClients.map((client) => (
                <div key={client.id} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm relative">
                  <div className="flex justify-between items-start gap-2 mb-2 min-w-0">
                    <Link
                      to={`/clients/${client.id}`}
                      className="text-lg font-semibold text-gray-900 hover:text-primary-600 cursor-pointer truncate flex-1"
                      title={`View client overview for ${client.name}`}
                    >
                      {client.name}
                    </Link>
                    <div className="flex space-x-1 flex-shrink-0"> {/* Smaller space for mobile icons */}
                      <Link
                        to={`/clients/edit/${client.id}`}
                        className="text-primary-600 hover:text-primary-900 p-1 rounded-full hover:bg-primary-50 transition duration-150 ease-in-out"
                        aria-label={`Edit ${client.name}`}
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path></svg>
                      </Link>
                      <button
                        onClick={() => handleDelete(client.id)}
                        className="text-red-600 hover:text-red-900 p-1 rounded-full hover:bg-red-50 transition duration-150 ease-in-out"
                        aria-label={`Delete ${client.name}`}
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path></svg>
                      </button>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 mb-1 break-words">
                    <span className="font-medium">Email:</span> {client.email ? (
                      <a href={`mailto:${client.email}`} className="text-gray-900 hover:text-primary-600 ml-1" title={`Email ${client.name}`}>
                        {client.email}
                      </a>
                    ) : 'N/A'}
                  </p>
                  <p className="text-sm text-gray-600 mb-1 break-words">
                    <span className="font-medium">Phone:</span> {client.phone ? (
                      <a href={`tel:${client.phone}`} className="text-gray-900 hover:text-primary-600 ml-1" title={`Call ${client.name}`}>
                        {client.phone}
                      </a>
                    ) : 'N/A'}
                  </p>
                  <p className="text-sm text-gray-600 break-words">
                    <span className="font-medium">Address:</span> {client.address ? (
                      <a
                        href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(client.address)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gray-900 hover:text-primary-600 ml-1"
                        title={`View ${client.name}'s address on Google Maps`}
                      >
                        {client.address}
                      </a>
                    ) : 'No address'}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {filteredClients.length > 0 && (
          <div className="mt-6 flex flex-col items-center space-y-4 sm:flex-row sm:justify-between sm:space-y-0 min-w-0">
            <div className="flex items-center space-x-2 flex-wrap justify-center sm:justify-start">
              <span className="text-sm text-gray-600 whitespace-nowrap">Items per page:</span>
              <select 
                value={itemsPerPage} 
                onChange={handleItemsPerPageChange}
                className="p-2 border border-gray-300 rounded-md text-sm"
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
            <div className="flex items-center space-x-2 flex-wrap justify-center">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 whitespace-nowrap"
              >
                Previous
              </button>
              <span className="text-sm text-gray-600 whitespace-nowrap">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 whitespace-nowrap"
              >
                Next
              </button>
            </div>
          </div>
        )}

        {/* Quotes Modal */}
        {isModalOpen && selectedClient && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[80vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200">
                <div className="flex justify-between items-center">
                  <h2 className="text-2xl font-bold text-primary-700">Quotes for {selectedClient.name}</h2>
                  <button
                    onClick={handleCloseModal}
                    className="text-gray-500 hover:text-gray-700 focus:outline-none"
                    aria-label="Close modal"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
                  </button>
                </div>
              </div>
              <div className="p-6">
                {quotesLoading ? (
                  <p className="text-gray-600 text-center py-4">Loading quotes...</p>
                ) : quotesError ? (
                  <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-md text-center" role="alert">
                    {quotesError}
                  </div>
                ) : clientQuotes.length === 0 ? (
                  <p className="text-gray-600 text-center py-4 border border-dashed border-gray-300 rounded-lg">
                    No quotes found for {selectedClient.name}.
                  </p>
                ) : (
                  <>
                    {/* Desktop Table View (hidden on small screens) */}
                    <div className="hidden md:block overflow-x-auto rounded-lg border border-gray-200 shadow-sm">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-blue-50">
                          <tr>
                            <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-blue-700 uppercase tracking-wider">Quote Name</th>
                            <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-blue-700 uppercase tracking-wider">Status</th>
                            <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-blue-700 uppercase tracking-wider">Total</th>
                            <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-blue-700 uppercase tracking-wider">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-100">
                          {clientQuotes.map((quote) => (
                            <tr key={quote.id} className="hover:bg-gray-50 transition duration-150 ease-in-out">
                              <td className="px-4 py-3 whitespace-nowrap">
                                <div className="text-sm font-medium text-gray-900">{quote.quoteName}</div>
                                <div className="text-xs text-gray-500">Expires: {quote.expirationDate ? new Date(quote.expirationDate).toLocaleDateString() : 'Not Set'}</div>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(quote.status)}`}>
                                  {quote.status}
                                </span>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-semibold text-gray-900">
                                ${parseFloat(quote.grandTotal || 0).toFixed(2)}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
                                <Link
                                  to={`/quotes/edit/${quote.id}`}
                                  className="text-primary-600 hover:text-primary-900 p-1 rounded-full hover:bg-primary-50 transition duration-150 ease-in-out"
                                  aria-label={`Edit ${quote.quoteName}`}
                                >
                                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path></svg>
                                </Link>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {/* Mobile Card View (visible on small screens, hidden on md and up) */}
                    <div className="md:hidden space-y-4">
                      {clientQuotes.map((quote) => (
                        <div key={quote.id} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm relative">
                          <div className="flex justify-between items-start mb-2">
                            <div className="text-lg font-semibold text-gray-900">
                              {quote.quoteName}
                            </div>
                            <Link
                              to={`/quotes/edit/${quote.id}`}
                              className="text-primary-600 hover:text-primary-900 p-1 rounded-full hover:bg-primary-50 transition duration-150 ease-in-out"
                              aria-label={`Edit ${quote.quoteName}`}
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path></svg>
                            </Link>
                          </div>
                          <p className="text-sm text-gray-600 mb-1">
                            <span className="font-medium">Expires:</span> {quote.expirationDate ? new Date(quote.expirationDate).toLocaleDateString() : 'Not Set'}
                          </p>
                          <p className="text-sm text-gray-600 mb-1">
                            <span className="font-medium">Status:</span> <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(quote.status)}`}>{quote.status}</span>
                          </p>
                          <p className="text-sm text-gray-600">
                            <span className="font-medium">Total:</span> <span className="font-semibold">${parseFloat(quote.grandTotal || 0).toFixed(2)}</span>
                          </p>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
              <div className="p-6 border-t border-gray-200 bg-gray-50">
                <button onClick={handleCloseModal} className="px-4 py-2 bg-gray-600 text-white font-medium rounded-lg shadow-md hover:bg-gray-700 w-full sm:w-auto">
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ClientListPage;
